import { Component, inject, OnInit } from '@angular/core';
import { PageBreadcrumbComponent, BreadcrumbItem } from '../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { SelectComponent } from '../../shared/components/form/select/select.component';
import { Toast } from 'primeng/toast';
import { AppDatePipe } from '../../shared/pipe/app-date.pipe';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { CommonService } from '../../services/common/common.service';
import { MasterdataService } from '../../services/master-data/masterdata.service';
import { LoaderService } from '../../services/loader/loader.service';
import { DATETIME_FORMAT_GROUPS, DateTimeService, DEFAULT_DATETIME_FORMAT } from '../../shared/services/date-time.service';

@Component({
  selector: 'app-profile',
  imports: [
    PageBreadcrumbComponent,
    SelectComponent,
    Toast,
    AppDatePipe,
  ],
  templateUrl: './profile.component.html',
  styles: ``
})
export class ProfileComponent implements OnInit {
  readonly breadcrumbs: BreadcrumbItem[] = [
    { label: 'User Profile' }
  ];
  userDetails$: Observable<any>;
  applicationList$: Observable<any>;

  private readonly store = inject(Store<any>);
  public userDetails: any;

  private readonly dateTimeService = inject(DateTimeService);
  private readonly commonService = inject(CommonService);
  private readonly masterDataService = inject(MasterdataService);
  private readonly loaderService = inject(LoaderService);
  private readonly messageService = inject(MessageService);
  readonly blankAvatar = '/images/user/Blank_profile.png';

  datetimeFormatGroups = DATETIME_FORMAT_GROUPS.map(group => ({
    label: group.label,
    options: group.options.map(format => ({
      value: format.key,
      label: format.label,
    })),
  }));
  selectedDatetimeFormat = this.dateTimeService.currentFormat;
  previewTimestamp = Date.now();

  constructor() {
    this.userDetails$ = this.store.pipe(select(state => state?.data?.data));
    this.applicationList$ = this.store.pipe(select(state => state?.lists?.application));
  }

  ngOnInit(): void {
    this.userDetails$.subscribe(data => {
      this.userDetails = {
        ...data,
        first_name: data?.first_name && data?.first_name !== '' ? data?.first_name : 'EDI',
        last_name: data?.last_name && data?.last_name !== '' ? data?.last_name : 'User',
      };
    });
  }

  get fullName(): string {
    return `${this.userDetails?.first_name || ''} ${this.userDetails?.last_name || ''}`.trim() || '—';
  }

  get initials(): string {
    const first = this.userDetails?.first_name?.[0] || '';
    const last = this.userDetails?.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  }

  get statusClasses(): string {
    switch (this.userDetails?.status) {
      case 'Active':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400';
      case 'Pending':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400';
      default:
        return 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400';
    }
  }

  get statusDotClasses(): string {
    switch (this.userDetails?.status) {
      case 'Active':
        return 'bg-emerald-500';
      case 'Pending':
        return 'bg-amber-500';
      default:
        return 'bg-rose-500';
    }
  }

  onImageChange(event: any) {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    this.loaderService.show();
    const formData = new FormData();
    formData.append('image', file);
    this.masterDataService.profilePicUpload(formData).subscribe({
      next: (res: any) => {
        this.loaderService.hide();
        this.userDetails.profile_image_url = res.profile_image_url;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message });
      },
      error: (err) => {
        this.loaderService.hide();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message });
      }
    });
  }

  onDatetimeFormatChange(format: string) {
    const previousFormat = this.dateTimeService.currentFormat;
    if (!format || format === previousFormat) {
      return;
    }
    this.dateTimeService.setFormat(format);
    this.commonService.updateDatetimeFormat(format).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: 'Date & time format updated.'
        });
      },
      error: () => {
        this.selectedDatetimeFormat = previousFormat;
        this.dateTimeService.setFormat(previousFormat || DEFAULT_DATETIME_FORMAT);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not save the date & time format.'
        });
      }
    });
  }

}
